import { SizedPlot } from 'features/timeSeries/experiment/SizedPlot'
import { TrainResponse } from 'features/timeSeries/experiment/types'
import { PlotData, Shape } from 'plotly.js'
import { FC, useMemo } from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'

const FG = CantileverTheme.colors.grayscale['100']
const GRID = CantileverTheme.colors.grayscale['600']
const RESIDUAL_COLOR = '#22d3ee'
const BAND_LINE_COLOR = '#f97316'

const Empty = styled.p`
  margin: 0;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

/**
 * Compact residual (actual − expected) trace over the TEST split only, for
 * the selected trained method — the held-out rows are the honest read on
 * whether the model generalizes, so train/val residuals are deliberately
 * excluded. A zero line and the ± residual_band bounds are drawn as
 * reference lines, making out-of-band excursions (the white dots on the main
 * chart) obvious at a glance.
 */
export const ResidualMiniPlot: FC<{
  result: TrainResponse
  methodKey: string
}> = ({ result, methodKey }) => {
  const method = result.methods?.[methodKey]
  const series = method?.series

  const band =
    method?.per_tag?.[result.target]?.residual_band ??
    method?.band_meta?.residual_band ??
    null

  const testPoints = useMemo(() => {
    if (!series?.ts?.length || !series.role?.length) return null
    const xs: string[] = []
    const ys: (number | null)[] = []
    for (let i = 0; i < series.ts.length; i++) {
      if (series.role[i] !== 'test') continue
      xs.push(series.ts[i])
      ys.push(series.residual?.[i] ?? null)
    }
    return xs.length ? { xs, ys } : null
  }, [series])

  if (!method) return null
  if (!testPoints) {
    return <Empty>No test rows in this split — nothing to residual-plot.</Empty>
  }

  const data: Partial<PlotData>[] = [
    {
      x: testPoints.xs,
      y: testPoints.ys,
      type: 'scatter',
      mode: 'lines',
      name: 'residual',
      line: { width: 1.1, color: RESIDUAL_COLOR },
      hovertemplate: '%{x}<br>residual %{y:.4f}<extra></extra>',
    },
  ]

  const shapes: Array<Partial<Shape>> = [
    {
      type: 'line',
      xref: 'paper',
      x0: 0,
      x1: 1,
      y0: 0,
      y1: 0,
      line: { color: GRID, width: 1 },
    },
  ]
  if (band !== null && Number.isFinite(band)) {
    for (const bound of [band, -band]) {
      shapes.push({
        type: 'line',
        xref: 'paper',
        x0: 0,
        x1: 1,
        y0: bound,
        y1: bound,
        line: { color: BAND_LINE_COLOR, width: 1, dash: 'dot' },
      })
    }
  }

  return (
    <SizedPlot
      $heightRem={9}
      data={data}
      layout={{
        paper_bgcolor: 'rgba(0,0,0,0)',
        plot_bgcolor: 'rgba(0,0,0,0)',
        margin: { l: 48, r: 12, t: 26, b: 30 },
        showlegend: false,
        title: {
          text: `Test residual · ${method.label ?? methodKey}${
            band !== null && Number.isFinite(band)
              ? ` (±band ${band.toPrecision(3)})`
              : ''
          }`,
          font: { color: FG, size: 11 },
          x: 0,
        },
        shapes,
        xaxis: { tickfont: { color: FG, size: 9 }, gridcolor: GRID },
        yaxis: { tickfont: { color: FG, size: 9 }, gridcolor: GRID },
      }}
    />
  )
}
